import { Injectable, signal } from '@angular/core';
import {
  apiFilter,
  Column,
  NestedColumn,
  ResponseData,
  Row,
  TableDataProvider,
  TableRequest,
  TableResponse,
} from '../../components/table/table.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StaticDataProviderService implements TableDataProvider {
  currData = signal<ResponseData>({
    rows: [],
    page: 1,
    pages: 0,
    pageSize: 10,
    schema: [],
    totalCount: 0,
  });

  fullData = signal<ResponseData>({
    rows: [],
    page: 1,
    pages: 0,
    pageSize: 10,
    schema: [],
    totalCount: 0,
  });

  tableView = signal<{
    sortByCols: Record<string, 'asc' | 'desc'>[];
    filters: apiFilter;
    query: string;
  }>({
    sortByCols: [],
    filters: {},
    query: '',
  });

  prevQuery = signal<string | undefined>(undefined);

  initialize(data: ResponseData): void {
    // apply deep copy to avoid having 2 arrays reference to the same object
    const dataCopy = structuredClone(data);

    this.fullData.set(dataCopy);
    this.currData.set(structuredClone(dataCopy)); // another deep copy here
  }

  fetchData(request: TableRequest): Observable<TableResponse> {
    const {
      page,
      pageSize,
      sortBy,
      filters,
      returnAll,
      search,
      getTotalCount,
      getAvailableFilters,
    } = request;

    if (getTotalCount && getAvailableFilters) {
      const freshData = this.fullData();

      this.currData.set({
        ...freshData,
        rows: [...freshData.rows],
      });

      this.tableView.set({
        sortByCols: [],
        filters: {},
        query: '',
      });

      this.prevQuery.set(undefined);
    }

    if (pageSize) {
      this.currData.set({
        ...this.currData(),
        pageSize,
      });
    }

    this.applySearch(search);

    if (sortBy) {
      this.applySorting(sortBy);
    }

    if (filters && Object.keys(filters).length > 0) {
      this.applyFilters(filters);
    }

    let startIndex = 0;
    let endIndex = this.currData().rows.length;
    if (!returnAll) {
      startIndex = (page - 1) * this.currData().pageSize;
      endIndex = startIndex + this.currData().pageSize;
    }

    const paginatedRows = this.currData().rows.slice(startIndex, endIndex);

    return new Observable<TableResponse>((observer) => {
      const response: TableResponse = {
        success: true,
        message: 'Data fetched successfully',
        messageCode: 'S0000',
        data: {
          page: page,
          pages: Math.ceil(
            this.currData().totalCount! / this.currData().pageSize
          ),
          pageSize: this.currData().pageSize,
          rows: paginatedRows,
          schema: this.currData().schema,
          filters: this.currData().filters,
          totalCount: getTotalCount ? this.currData().totalCount : undefined,
        },
      };
      observer.next(response);
      observer.complete();
    });
  }

  applySorting(sortBy: Record<string, 'asc' | 'desc'>[]) {
    const rowsToSort = [...this.currData().rows];

    rowsToSort.sort((a: Row, b: Row) => {
      for (const sortCondition of sortBy) {
        const columnName = Object.keys(sortCondition)[0];
        const direction = sortCondition[columnName];
        if (a[columnName] < b[columnName]) {
          return direction === 'asc' ? -1 : 1;
        } else if (a[columnName] > b[columnName]) {
          return direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    this.currData.update((data) => ({
      ...data,
      rows: rowsToSort,
    }));

    this.tableView.set({
      ...this.tableView(),
      sortByCols: [...this.tableView().sortByCols, ...sortBy],
    });
  }

  applySearch(query: string | undefined) {
    if (!query && !this.prevQuery()) {
      return;
    }

    console.log('full Data on search', this.fullData());
    this.prevQuery.set(query);
    if (!query) {
      this.currData.set(this.fullData());
      this.tableView.set({
        ...this.tableView(),
        query: '',
      });
      return;
    }

    query = query.toLowerCase();

    const schema = this.currData().schema;
    const sourceRows = this.fullData().rows;

    const filteredRows = sourceRows.filter((row: Row) => {
      for (const col of schema) {
        if (col.ignore) continue;

        if (col.type === 'single') {
          if (this.searchCell(row, col as Column, query)) return true;
        } else if (col.type === 'group') {
          const groupCol = col as NestedColumn;

          for (const subCol of groupCol.groupColumns) {
            if (this.searchCell(row, subCol, query)) return true;
          }
        }
      }
      return false;
    });

    this.currData.set({
      ...this.currData(),
      rows: filteredRows,
      totalCount: filteredRows.length,
    });

    this.tableView.set({
      ...this.tableView(),
      query,
    });
  }

  applyFilters(filters: Record<string, string[]>) {
    // TODO: Change when filters get fixed
    const colName = 'city';
    const options = filters['cities'];
    const filteredRows = this.fullData().rows.filter((row: Row) => {
      return options?.includes(String(row[colName]));
    });

    this.currData.set({
      ...this.currData(),
      rows: filteredRows,
      totalCount: filteredRows.length,
    });

    this.tableView.set({
      ...this.tableView(),
      filters: { ...this.tableView().filters, ...filters },
    });
  }

  searchCell(row: Row, col: Column, query: string): boolean {
    return String(row[col.name]).toLowerCase().includes(query);
  }
}
